<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import {useRoute} from 'vue-router'
import { MapSource, MapLayer } from '@/components/map'
import { API, HOST } from '@/utils/http'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'
import { buildOrderTileUrl } from '@/lib/tileUrl'

const store = useStore()
const route = useRoute()
const map = computed(() => props.map)
const layerManager = useLayerManager(map)

const emit = defineEmits(['isLoaded', 'statusUpdated'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: false,
    default: () => ({})
  },
  layer: {
    type: Object,
    required: false,
    default: () => ({})
  },
  isVisible: {
    type: Boolean,
    default: true
  }
})

// Unified layer IDs
const rasterSourceId = computed(() => createSourceId(props.groupId, 'raster-source'))
const rasterLayerId = computed(() => createLayerId(props.groupId, 'raster'))

const currentStatus = ref(props.layer?.layer?.status ?? props.layer?.status ?? 0)
const pollingInterval = ref(null)
const orgId = computed(() => route.params.org_id)
const selectedProductBundle = ref('visual')

const planetOrderId = computed(() => {
  return props.layer?.layer?.planet_order_id
})

// Determine available product bundles
// Note: Geometry rendering is now handled in the parent LayerItemMapLayer component

const availableProductBundles = computed(() => {
  const bundles = new Set()

  // Check for new multi-product format
  if (props.layer?.layer?.data?.products) {
    props.layer.layer.data.products.forEach(product => {
      if (product.product_bundle) {
        bundles.add(product.product_bundle)
      }
    })
  }
  // Fall back to legacy single product format
  else if (props.layer?.layer?.data?.product_bundle) {
    bundles.add(props.layer.layer.data.product_bundle)
  }

  // Default to visual if nothing found
  if (bundles.size === 0) {
    bundles.add('visual')
  }

  return Array.from(bundles)
})

// Track tile errors to reduce console spam
const tileErrorCount = ref(0)
// Limit tile error logging to 5 to avoid excessive console spam during tile loading failures
const MAX_CONSECUTIVE_TILE_ERRORS_TO_AVOID_SPAM = 5

const fetchLayerStatus = async () => {
  if (!props.layer.code || !orgId.value) return

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/layers/${props.layerId.replace(/^layer-/, '')}`
    })

    if (response.data && response.data.layer) {
      const newStatus = response.data.layer.status
      if (newStatus !== currentStatus.value) {
        currentStatus.value = newStatus
        emit('statusUpdated', newStatus)

        // Update the layer in the store
        store.dispatch('layers/updateOrgLayer', {
          orgId: orgId.value,
          layer: response.data
        })

        // Stop polling if status is complete (0) or failed (negative)
        if (newStatus === 0 || newStatus < 0) {
          stopPolling()
        }
      }
    }
  } catch (error) {
    console.error('Error fetching layer status:', error)
  }
}

const startPolling = () => {
  // Only poll if status is in progress (positive and not 0)
  if (currentStatus.value > 0) {
    // Poll every 30 seconds
    pollingInterval.value = setInterval(fetchLayerStatus, 30000)
    // Also fetch immediately
    fetchLayerStatus()
  }
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

// Watch for product bundle changes to refresh the map source
watch(selectedProductBundle, (newBundle) => {
  if (map.value && parseInt(currentStatus.value) === 0) {
    // Reset error count when switching bundles
    tileErrorCount.value = 0

    // Force map to reload tiles with new product bundle
    if (map.value.getSource(rasterSourceId.value)) {
      const url = buildOrderTileUrl(orgId.value, props.layer?.layer?.code, props.layer?.layer, newBundle)
      if (url) map.value.getSource(rasterSourceId.value).setTiles([url])
    }
  }
})

// Tile URL — direct-S3 if storage_bucket is set on the row, else Tulum proxy.
const tileUrl = computed(() => {
  if (!planetOrderId.value) return null
  return buildOrderTileUrl(orgId.value, props.layer?.layer?.code, props.layer?.layer, selectedProductBundle.value)
})

// Watch for product bundle changes from detail page via store
const storeProductBundle = computed(() => store.getters['layers/getOrderProductBundle'](props.layerId))
watch(storeProductBundle, (bundle) => {
  if (bundle && bundle !== selectedProductBundle.value) {
    selectedProductBundle.value = bundle
  }
})

// Setup tile error handling
const setupTileErrorHandling = () => {
  if (!map.value) return

  // Listen for tile errors and suppress console spam
  const handleSourceError = (e) => {
    if (e.source?.id === rasterSourceId.value && e.error?.status === 404) {
      // Only log first few errors to avoid spam
      if (tileErrorCount.value < MAX_CONSECUTIVE_TILE_ERRORS_TO_AVOID_SPAM) {
        tileErrorCount.value++
        if (tileErrorCount.value === MAX_CONSECUTIVE_TILE_ERRORS_TO_AVOID_SPAM) {
          console.warn(`Suppressing further 404 tile errors for layer ${props.layerId}`)
        }
      }
      // Prevent default error handling to suppress console error
      e.preventDefault?.()
    }
  }

  map.value.on('error', handleSourceError)

  // Store handler for cleanup
  return () => {
    map.value?.off('error', handleSourceError)
  }
}

let cleanupErrorHandler = null

onMounted(async () => {
  // Register layer with layer manager
  layerManager.registerLayer(props.groupId, rasterLayerId.value)

  emit('isLoaded', true)
  // Set initial product bundle
  if (availableProductBundles.value.length > 0) {
    selectedProductBundle.value = availableProductBundles.value[0]
  }

  // Setup error handling when map is ready
  if (map.value && parseInt(currentStatus.value) === 0) {
    cleanupErrorHandler = setupTileErrorHandling()
  }

  startPolling()
})

onUnmounted(() => {
  // Unregister layer from layer manager
  layerManager.unregisterLayer(props.groupId, rasterLayerId.value)

  stopPolling()
  // Clean up error handler
  cleanupErrorHandler?.()
})
</script>

<template>
  <div>
  <div v-if="map">
    <!-- Raster tiles -->
    <MapSource
      v-if="parseInt(currentStatus) === 0 && tileUrl"
      :map="map"
      type="raster"
      :id="rasterSourceId"
      :options="{
        tiles: [tileUrl],
        tileSize: 256,
      }"
    >
      <MapLayer
        :map="map"
        :id="rasterLayerId"
        type="raster"
        :source="rasterSourceId"
        :before-id="beforeId"
        :group-id="groupId"
        :options="{
          ...options,
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        }"
      ></MapLayer>
    </MapSource>
  </div>
  </div>
</template>
